import { UseFormReturn } from 'react-hook-form';
import {
  AssetOrigin,
  LANGUAGE_METADATA,
  Languages,
  MAX_CHARS_DEFAULT,
  Music,
  VideoSettings,
  Voiceover,
} from '@monorepo/types';
import { Button, Col, Collapse, Divider, Flex, Row, Typography } from 'antd';
import {
  FormRadioButtonGroup,
  FormSelect,
  FormTextArea,
  Space,
} from '@monorepo/react-components';
import { useStore } from '../../helpers/use-store';
import { FormVoiceover } from '../../components/form/form-voiceover';
import {
  AudioOutlined,
  CaretRightOutlined,
  CustomerServiceOutlined,
  FileTextOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  ReadOutlined,
  RobotOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { SAMPLE_SCRIPT } from './video-form-types';
import AssetStyles from '../../components/asset-styles/asset-styles';

import './base-video-form.scss';
import { FormMusic } from '../../components/form/form-music';
import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { startCase } from 'lodash';
import { useResponsiveReady } from '../../hooks/use-responive-ready';
import { useTranslation } from 'react-i18next';
import ScriptGenerateModal, {
  ScriptGenerateModalActions,
} from './ai-script-writer/ai-script-writer';
import { showInfoModal } from '../../helpers/fast-modal';
import { VideoOptionsForm } from './video-options/video-options-form';
import FormAudioUpload from '../../components/form-audio-upload/form-audio-upload';
import { STYLE_MAPPING } from '../ai-image-styles/ai-image-styles';

const { Text } = Typography;
const { Panel } = Collapse;

// Style constants
const sectionTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '8px',
  color: '#1f1f1f',
  fontSize: '16px',
  fontWeight: 500,
};

const iconStyle = {
  fontSize: '20px',
  color: '#1890ff',
};

const sectionStyle = {
  marginBottom: '10px',
};

export const SectionTitle = ({ icon, text }) => (
  <div style={sectionTitleStyle}>
    {icon}
    <span>{text}</span>
  </div>
);

export const SectionBorder = ({ children, style = {} }) => {
  return (
    <div
      style={{
        border: `1px solid #e3e3e3`,
        padding: `5px 0`,
        borderRadius: `6px`,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export const getLanguageOptions = () => {
  return Object.entries(Languages).map(([name, code]) => ({
    label: `${LANGUAGE_METADATA[code].flag} ${LANGUAGE_METADATA[code].name} (${LANGUAGE_METADATA[code].nativeText})`,
    value: code,
  }));
};

export type BaseVideoFormType = VideoSettings & { script?: string };

interface Props {
  form: UseFormReturn<BaseVideoFormType & { subjectType?: string }>;
  hideScript?: boolean;
  collapsed?: boolean;
  sectionText?: boolean;
  extraContentSection?: ReactNode;
  showScriptWriter?: boolean;
  useCollapse?: boolean;
}

const sections = [
  'content',
  'language',
  'visual-style',
  'voiceover',
  // 'background-music',
];
const BaseVideoForm: FunctionComponent<Props> = ({
  form,
  collapsed,
  hideScript,
  extraContentSection,
  sectionText,
  showScriptWriter = false,
  useCollapse = true,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<ScriptGenerateModalActions>(null);
  const [expandedSection, setExpandedSections] = useState<string[]>(
    collapsed ? [] : sections
  );
  const {
    dataStore: { voiceoverStore, musicStore, userStore },
  } = useStore();
  const { isMobile } = useResponsiveReady();

  useEffect(() => {
    if (collapsed) {
      setExpandedSections([]);
    } else {
      setExpandedSections(sections);
    }
  }, [collapsed]);

  const music = musicStore.get(form.getValues('backgroundMusicId') || '');
  const voiceOver = voiceoverStore.get(form.getValues('voiceoverId'));

  const subjectType = form.getValues('subjectType');
  const contentSectionText = subjectType
    ? t(`seriesForm.subject.${subjectType}`, {
        defaultValue: startCase(subjectType),
      })
    : '';
  const languageSectionText = form.getValues('language')
    ? Object.keys(Languages).find(
        (key) => Languages[key] === form.getValues('language')
      )
    : '';
  const assetStyleValue = form.getValues('assetStyle');
  const styleEntry = assetStyleValue
    ? STYLE_MAPPING[assetStyleValue as keyof typeof STYLE_MAPPING]
    : null;
  const visualStylesSectionText = styleEntry
    ? t(`aiStyles.${styleEntry.nameKey}`, { defaultValue: styleEntry.name })
    : '';
  let musicSectionText = '';
  if (music && form.getValues('backgroundMusicId')) {
    musicSectionText = startCase(music.name);
  } else if (form.getValues('backgroundMusicUrl')) {
    const url = form.getValues('backgroundMusicUrl') as string;
    musicSectionText = url.split('/').pop() || t('baseVideoForm.customAudio');
  }
  const voiceOverSectionText =
    voiceOver && form.getValues('voiceoverId') ? startCase(voiceOver.name) : '';

  const handleClick = () => {
    modalRef.current?.open();
  };

  const backgroundMusicId = form.getValues('backgroundMusicId');

  useEffect(() => {
    if (backgroundMusicId) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore huy vrot
      form.setValue('audioFile', '');
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore huy vrot
      form.setValue('backgroundMusicUrl', '');
    }
  }, [backgroundMusicId]);

  const formItems = [
    {
      key: 'content',
      label: (
        <SectionTitle
          icon={<ReadOutlined style={iconStyle} />}
          text={t('baseVideoForm.sections.content')}
        />
      ),
      extra: sectionText && <Text type="secondary">{contentSectionText}</Text>,
      children: (
        <Space direction="vertical">
          {extraContentSection}
          {!hideScript ? (
            <>
              <Flex justify={'space-between'}>
                <SectionTitle
                  icon={<FileTextOutlined style={iconStyle} />}
                  text={
                    <>
                      {t('baseVideoForm.script')}
                      <InfoCircleOutlined
                        style={{ marginLeft: '5px' }}
                        onClick={() => {
                          showInfoModal({
                            title: t('baseVideoForm.scriptTipsTitle'),
                            content: (
                              <div style={{ fontSize: '14px' }}>
                                <Typography.Paragraph>
                                  {t('baseVideoForm.scriptTipsLine1')}
                                </Typography.Paragraph>

                                <Typography.Paragraph>
                                  {t('baseVideoForm.scriptTipsExample1')}
                                </Typography.Paragraph>

                                <Typography.Paragraph>
                                  {t('baseVideoForm.scriptTipsLine2')}
                                </Typography.Paragraph>

                                <Typography.Paragraph>
                                  {t('baseVideoForm.scriptTipsExample2')}
                                </Typography.Paragraph>
                              </div>
                            ),
                            width: 500,
                            okText: t('baseVideoForm.scriptTipsGotIt'),
                          });
                        }}
                      />
                    </>
                  }
                />
                {showScriptWriter ? (
                  <Button
                    type="primary"
                    onClick={handleClick}
                    icon={<RobotOutlined />}
                    shape={'round'}
                  >
                    {t('baseVideoForm.aiScriptWriter')}
                  </Button>
                ) : null}
              </Flex>
              <FormTextArea
                form={form}
                controllerProps={{
                  name: 'script',
                  rules: {
                    required: true,
                    minLength: {
                      message: t('baseVideoForm.scriptMinLength'),
                      value: 150,
                    },
                  },
                }}
                textareaProps={{
                  className: 'script-area',
                  showCount: true,
                  maxLength:
                    userStore.currentUser.applicationConfig
                      ?.maxCharsForScript || MAX_CHARS_DEFAULT,
                  placeholder: SAMPLE_SCRIPT,
                  rows: 12,
                  style: { width: '100%' },
                }}
              />
            </>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'language',
      label: (
        <SectionTitle
          icon={<GlobalOutlined style={iconStyle} />}
          text={t('baseVideoForm.sections.language')}
        />
      ),
      extra: sectionText && <Text type="secondary">{languageSectionText}</Text>,
      children: (
        <FormSelect
          form={form}
          placeholder={t('baseVideoForm.languagePlaceholder')}
          options={getLanguageOptions()}
          controllerProps={{
            name: 'language',
            rules: { required: true },
          }}
        />
      ),
    },
    {
      key: 'visual-style',
      label: (
        <SectionTitle
          icon={<PictureOutlined style={iconStyle} />}
          text={t('baseVideoForm.sections.visualStyle')}
        />
      ),
      extra: sectionText && (
        <Text type="secondary">{visualStylesSectionText}</Text>
      ),
      style: { flex: 1, display: 'flex', flexDirection: 'column' },
      children: (
        <Space
          direction="vertical"
          size="large"
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <FormRadioButtonGroup
            options={[
              {
                text: t('aiStyles.aiGeneratedImages'),
                value: AssetOrigin.AIImages,
              },
            ]}
            form={form}
            controllerProps={{
              name: 'assetOrigin',
              rules: { required: true },
            }}
          />
          <div>
            <AssetStyles
              assetOrigin={form.watch('assetOrigin')}
              value={form.watch('assetStyle')}
              onChange={(value) => form.setValue('assetStyle', value)}
            />
          </div>
        </Space>
      ),
    },
    {
      key: 'voiceover',
      label: (
        <SectionTitle
          icon={<AudioOutlined style={iconStyle} />}
          text={t('baseVideoForm.sections.voiceover')}
        />
      ),
      extra: sectionText && (
        <Text type="secondary">{voiceOverSectionText}</Text>
      ),
      children: (
        <FormVoiceover
          language={form.watch('language') as Languages}
          gridColumns={isMobile ? 1 : 2}
          form={form}
          voiceovers={voiceoverStore.items}
          controllerProps={{
            name: 'voiceoverId',
            rules: {
              required: t('baseVideoForm.voiceoverRequired'),
            },
          }}
        />
      ),
    },
    {
      key: 'background-music',
      label: (
        <SectionTitle
          icon={<CustomerServiceOutlined style={iconStyle} />}
          text={t('baseVideoForm.sections.backgroundMusic')}
        />
      ),
      extra: sectionText && <Text type="secondary">{musicSectionText}</Text>,
      children: (
        <>
          <FormMusic
            gridColumns={isMobile ? 1 : 2}
            form={form}
            musics={musicStore.items}
            controllerProps={{
              name: 'backgroundMusicId',
              rules: { required: false },
            }}
          />
          <Divider>{t('baseVideoForm.orUploadManually')}</Divider>
          <FormAudioUpload
            existingUrl={form.watch('backgroundMusicUrl')}
            form={form}
            controllerProps={{
              name: 'audioFile',
              rules: { required: false },
            }}
          />
        </>
      ),
    },
    {
      key: 'options',
      label: (
        <SectionTitle
          icon={<SettingOutlined style={iconStyle} />}
          text={t('baseVideoForm.sections.videoSettings')}
        />
      ),
      children: <VideoOptionsForm form={form} />,
    },
  ];

  return (
    <div className="base-video-form">
      <Row gutter={[24, 24]} style={{ display: 'flex', flexWrap: 'wrap' }}>
        <Col
          xs={24}
          lg={24}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          {useCollapse ? (
            <Collapse
              expandIcon={({ isActive }) => {
                return (
                  <Button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      padding: 0,
                      border: 'none',
                    }}
                  >
                    <CaretRightOutlined
                      style={{ fontSize: '14px', marginTop: '2px' }}
                      rotate={isActive ? 90 : 0}
                    />
                  </Button>
                );
              }}
              activeKey={expandedSection}
              bordered={false}
              onChange={(keys) => setExpandedSections(keys)}
              items={formItems}
            />
          ) : (
            <Space
              classNames={{ item: 'form-item' }}
              direction="vertical"
              size="large"
              style={{ width: '100%' }}
            >
              {formItems.map((item) => (
                <div key={item.key} style={sectionStyle}>
                  <div
                    className={'form-item-title'}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    {item.label}
                    {item.extra}
                  </div>
                  {item.children}
                </div>
              ))}
            </Space>
          )}
        </Col>
      </Row>
      {showScriptWriter ? (
        <ScriptGenerateModal ref={modalRef} form={form} />
      ) : null}
    </div>
  );
};

export default BaseVideoForm;
